/**
 * Session protocol (`Message`) encode/decode.
 *
 * Field numbers mirror message.proto; they are protocol and must not be
 * renumbered. Only the subset the browser client needs is implemented, and
 * unknown fields are skipped, which is what keeps this forward-compatible with
 * newer peers.
 */
import { Writer, Reader, eachField } from "./wire.js";
/** Message.union field numbers. */
export const MSG = {
    signed_id: 3,
    public_key: 4,
    test_delay: 5,
    video_frame: 6,
    login_request: 7,
    login_response: 8,
    hash: 9,
    mouse_event: 10,
    audio_frame: 11,
    cursor_data: 12,
    cursor_position: 13,
    cursor_id: 14,
    key_event: 15,
    clipboard: 16,
    file_action: 17,
    file_response: 18,
    misc: 19,
    message_box: 21,
    peer_info: 25,
    auth_2fa: 27,
    voice_call_request: 23,
    voice_call_response: 24,
    screenshot_request: 29,
    screenshot_response: 30,
    pointer_device_event: 26,
    multi_clipboards: 28,
    switch_sides_request: 22,
    terminal_action: 31,
    terminal_response: 32,
};
/** VideoFrame.union field numbers -> codec name. */
export const VIDEO_CODEC = {
    6: "vp9",
    10: "h264",
    11: "h265",
    12: "vp8",
    13: "av1",
};
export const ControlKey = {
    Unknown: 0, Alt: 1, Backspace: 2, CapsLock: 3, Control: 4, Delete: 5,
    DownArrow: 6, End: 7, Escape: 8, F1: 9, F10: 10, F11: 11, F12: 12, F2: 13,
    F3: 14, F4: 15, F5: 16, F6: 17, F7: 18, F8: 19, F9: 20, Home: 21,
    LeftArrow: 22, Meta: 23, Option: 24, PageDown: 25, PageUp: 26, Return: 27,
    RightArrow: 28, Shift: 29, Space: 30, Tab: 31, UpArrow: 32,
    Numpad0: 33, Numpad1: 34, Numpad2: 35, Numpad3: 36, Numpad4: 37,
    Numpad5: 38, Numpad6: 39, Numpad7: 40, Numpad8: 41, Numpad9: 42,
    Cancel: 43, Clear: 44, Menu: 45, Pause: 46, Insert: 58,
    Snapshot: 57, Help: 59, Scroll: 62, NumLock: 63, RWin: 64, Apps: 65,
    Multiply: 66, Add: 67, Subtract: 68, Decimal: 69, Divide: 70, Equals: 71,
    NumpadEnter: 72, RShift: 73, RControl: 74, RAlt: 75,
    CtrlAltDel: 100, LockScreen: 101,
};
export const KeyboardMode = { Legacy: 0, Map: 1, Translate: 2, Auto: 3 };
/** OptionMessage.BoolOption */
export const BoolOption = { NotSet: 0, No: 1, Yes: 2 };
/** Values are NOT sequential in message.proto: NotSet=0, Low=2, Balanced=3, Best=4. */
export const ImageQuality = { NotSet: 0, Low: 2, Balanced: 3, Best: 4 };
// ---------------------------------------------------------------- outgoing --
export function encodePublicKey(asymmetric, symmetric) {
    const inner = new Writer().bytes(1, asymmetric).bytes(2, symmetric).finish();
    return new Writer().message(MSG.public_key, inner).finish();
}
export const PreferCodec = { Auto: 0, VP9: 1, H264: 2, H265: 3, VP8: 4, AV1: 5 };
/**
 * SupportedDecoding tells the peer which codecs we can decode, so it picks one
 * we can actually render. Field numbers from message.proto's SupportedDecoding.
 */
function encodeSupportedDecoding(o) {
    // ability_* fields are int32: 0 = unsupported, 1 = supported.
    return new Writer()
        .int32(1, o.vp9 ? 1 : 0) // ability_vp9
        .int32(2, o.h264 ? 1 : 0) // ability_h264
        .int32(3, o.h265 ? 1 : 0) // ability_h265
        .int32(4, o.prefer) // prefer
        .int32(5, o.vp8 ? 1 : 0) // ability_vp8
        .int32(6, o.av1 ? 1 : 0) // ability_av1
        .finish();
}
export function encodeOptionMessage(o) {
    const w = new Writer();
    if (o.imageQuality)
        w.int32(1, o.imageQuality);
    if (o.lockAfterSessionEnd !== undefined) {
        w.int32(2, o.lockAfterSessionEnd ? BoolOption.Yes : BoolOption.No);
    }
    if (o.privacyMode !== undefined) {
        w.int32(4, o.privacyMode ? BoolOption.Yes : BoolOption.No);
    }
    if (o.blockInput !== undefined) {
        w.int32(5, o.blockInput ? BoolOption.Yes : BoolOption.No);
    }
    if (o.showRemoteCursor !== undefined) {
        w.int32(3, o.showRemoteCursor ? BoolOption.Yes : BoolOption.No);
    }
    if (o.customImageQuality)
        w.int32(6, o.customImageQuality);
    if (o.disableAudio !== undefined)
        w.int32(7, o.disableAudio ? BoolOption.Yes : BoolOption.No);
    if (o.disableClipboard !== undefined) {
        w.int32(8, o.disableClipboard ? BoolOption.Yes : BoolOption.No);
    }
    if (o.enableFileTransfer !== undefined) {
        w.int32(9, o.enableFileTransfer ? BoolOption.Yes : BoolOption.No);
    }
    if (o.supportedDecoding)
        w.message(10, encodeSupportedDecoding(o.supportedDecoding));
    if (o.customFps)
        w.int32(11, o.customFps);
    if (o.disableKeyboard !== undefined) {
        w.int32(12, o.disableKeyboard ? BoolOption.Yes : BoolOption.No);
    }
    return w.finish();
}
export function encodeLoginRequest(o) {
    const w = new Writer()
        .string(1, o.username)
        .bytes(2, o.password)
        .string(4, o.myId)
        .string(5, o.myName);
    if (o.options)
        w.message(6, encodeOptionMessage(o.options));
    switch (o.connType) {
        case "file_transfer":
            // FileTransfer { string dir = 1; bool show_hidden = 2; }
            w.message(7, new Writer().string(1, o.fileTransferDir ?? "").bool(2, o.showHidden ?? false).finish());
            break;
        case "view_camera":
            w.message(15, new Writer().finish());
            break;
        case "terminal":
            w.message(16, new Writer().finish());
            break;
        default:
            break;
    }
    w.bool(9, o.videoAckRequired);
    if (o.sessionId)
        w.int64(10, o.sessionId);
    w.string(11, o.version);
    // OSLogin { username = 1; password = 2 }
    if (o.osUsername || o.osPassword) {
        w.message(12, new Writer().string(1, o.osUsername ?? "").string(2, o.osPassword ?? "").finish());
    }
    w.string(13, o.myPlatform ?? "Web");
    return new Writer().message(MSG.login_request, w.finish()).finish();
}
export function encodeMouseEvent(o) {
    const w = new Writer()
        .int32(1, o.mask)
        .sint32(2, o.x)
        .sint32(3, o.y)
        .packedVarints(4, o.modifiers);
    return new Writer().message(MSG.mouse_event, w.finish()).finish();
}
export function encodeKeyEvent(o) {
    // Field order mirrors rust-protobuf, which emits the regular fields first
    // and the oneof last. Order is semantically irrelevant to a parser, but
    // matching it keeps the byte-for-byte conformance tests meaningful.
    const w = new Writer()
        .bool(1, o.down)
        .bool(2, o.press)
        .packedVarints(8, o.modifiers)
        .int32(9, o.mode);
    // Exactly one of the oneof may be set.
    if (o.controlKey !== undefined)
        w.tag(3, 0 /* WireType.Varint */).varint(o.controlKey);
    else if (o.chr !== undefined)
        w.uint32(4, o.chr);
    else if (o.unicode !== undefined)
        w.uint32(5, o.unicode);
    else if (o.seq !== undefined)
        w.string(6, o.seq);
    return new Writer().message(MSG.key_event, w.finish()).finish();
}
/** Clipboard { compress = 1, content = 2, ... } */
export function encodeClipboardText(text) {
    const inner = new Writer().bytes(2, new TextEncoder().encode(text)).finish();
    return new Writer().message(MSG.clipboard, inner).finish();
}
export function encodeTestDelay(time, fromClient, lastDelay = 0, targetBitrate = 0) {
    const inner = new Writer()
        .int64(1, time)
        .bool(2, fromClient)
        .uint32(3, lastDelay)
        .uint32(4, targetBitrate)
        .finish();
    return new Writer().message(MSG.test_delay, inner).finish();
}
/** Misc wrappers --------------------------------------------------------- */
function misc(field, inner) {
    const m = new Writer().message(field, inner).finish();
    return new Writer().message(MSG.misc, m).finish();
}
function miscBool(field, value) {
    const m = new Writer().boolPresent(field, value).finish();
    return new Writer().message(MSG.misc, m).finish();
}
/**
 * A Misc variant carrying a scalar.
 *
 * Written even when zero: Misc's members are a `oneof`, so leaving a zero off
 * the wire - which is what proto3 normally does - means no variant at all, and
 * the peer ignores the message. That is how "stop following the screen in use"
 * became a message that did nothing.
 */
function miscInt(field, value) {
    const m = new Writer().int32Present(field, value).finish();
    return new Writer().message(MSG.misc, m).finish();
}
function miscUint(field, value) {
    const m = new Writer().int32Present(field, value).finish();
    return new Writer().message(MSG.misc, m).finish();
}
export const encodeRefreshVideo = () => miscBool(10, true);
export const encodeVideoReceived = () => miscBool(12, true);
export const encodeRefreshVideoDisplay = (display) => miscInt(31, display);
/** SwitchDisplay { display = 1, x = 2, y = 3, width = 4, height = 5, ... } */
export function encodeSwitchDisplay(display, width = 0, height = 0) {
    const inner = new Writer()
        .int32(1, display)
        .sint32(2, 0)
        .sint32(3, 0)
        .int32(4, width)
        .int32(5, height)
        .finish();
    return misc(5, inner);
}
export function encodeOptionUpdate(o) {
    return misc(7, encodeOptionMessage(o));
}
export const encodeRestartRemoteDevice = () => miscBool(14, true);
/**
 * Asks the machine to run its RustDesk service elevated.
 *
 * `direct` raises a UAC prompt that someone at the machine must accept.
 * Without elevation the remote side cannot interact with elevated windows
 * (an admin dialog swallows every click), which is what "cannot control
 * anything after a UAC prompt appears" looks like.
 */
export function encodeElevationDirect() {
    const req = new Writer().bool(1, true).finish();
    return misc(18, req);
}
/** Elevates using administrator credentials, with no prompt at the machine. */
export function encodeElevationWithLogon(username, password) {
    const logon = new Writer().string(1, username).string(2, password).finish();
    const req = new Writer().message(2, logon).finish();
    return misc(18, req);
}
export const encodeCloseReason = (reason) => {
    const m = new Writer().string(9, reason).finish();
    return new Writer().message(MSG.misc, m).finish();
};
export function encodeChat(text) {
    const inner = new Writer().string(1, text).finish();
    return misc(4, inner);
}
export function encodeAuth2FA(code) {
    const inner = new Writer().string(1, code).finish();
    return new Writer().message(MSG.auth_2fa, inner).finish();
}
/** Screenshot, voice and displays ----------------------------------------- */
/** Asks the peer for a still of one display. */
export function encodeScreenshotRequest(display, sid = "") {
    const inner = new Writer().int32(1, display).string(2, sid).finish();
    return new Writer().message(MSG.screenshot_request, inner).finish();
}
export function encodeVoiceCallRequest(isConnect, reqTimestamp) {
    const inner = new Writer().int64(1, reqTimestamp).bool(2, isConnect).finish();
    return new Writer().message(MSG.voice_call_request, inner).finish();
}
/**
 * Blanks the machine's own screen.
 *
 * Not an option: peers at 1.2.4 and later ignore `OptionMessage.privacy_mode`
 * entirely and act only on this. `implKey` names which implementation to use -
 * the peer lists what it has in `PeerInfo.platformAdditions`, under
 * `supported_privacy_mode_impl`. An empty key lets the peer choose.
 */
export function encodeTogglePrivacyMode(implKey, on) {
    return misc(33, new Writer().string(1, implKey).bool(2, on).finish());
}
/**
 * Adds or removes a virtual display.
 *
 * A machine with no real graphics adapter - a VM, a headless server - captures
 * whatever its basic display driver offers, which is often one low-refresh
 * screen. A virtual display gives it a real one, and is also what makes a
 * second screen possible where there is only one.
 */
function parseResolution(raw) {
    const out = { width: 0, height: 0 };
    eachField(raw, (field, _wire, r) => {
        if (field === 1) {
            out.width = r.int32();
            return true;
        }
        if (field === 2) {
            out.height = r.int32();
            return true;
        }
        return;
    });
    return out;
}
/**
 * Sets a screen's resolution on the machine.
 *
 * Separate from switching screens, where a size means "change it": the two
 * share a message and conflating them made every switch reconfigure the
 * machine's display.
 */
export function encodeChangeResolution(display, width, height) {
    return misc(36, new Writer()
        .int32(1, display)
        .message(2, new Writer().int32(1, width).int32(2, height).finish())
        .finish());
}
/**
 * Tells the machine what frame rate this client can actually keep up with.
 *
 * The desktop client measures its own decoding and reports it, and the machine
 * caps what it encodes to that. A client that never reports it leaves the
 * machine guessing from network delay alone.
 */
export const encodeAutoAdjustFps = (fps) => miscInt(28, fps);
/** Asks the machine to stop holding back frames when the picture is still. */
export const encodeFullSpeedFps = (fps) => miscInt(27, fps);
/**
 * Follow whichever screen the person at the machine is using.
 *
 * With this on, moving to another monitor there moves the view here.
 */
export const encodeFollowCurrentDisplay = (display) => miscInt(38, display);
/** Restarts the machine. */
export const encodeRestartRemote = () => miscBool(14, true);
/**
 * Declines a request to swap roles.
 *
 * A machine can ask to control this end instead. A browser has no screen to
 * hand over, so the answer is always to stay as we are - said explicitly, so
 * the machine is not left waiting.
 */
export const encodeSwitchBack = () => miscBool(22, true);
/** Stops RustDesk on the machine, ending every session on it. */
export const encodeStopService = () => miscBool(17, true);
/**
 * A two-finger pan, as a trackpad or touchscreen produces.
 *
 * Distinct from a wheel: a pan carries a position and a delta, so the machine
 * scrolls the thing under the pointer by exactly that much rather than by
 * whole notches.
 */
export function encodeTouchPan(phase, x, y) {
    const field = phase === "start" ? 2 : phase === "update" ? 3 : 4;
    const pan = new Writer().int32Present(1, x).int32Present(2, y).finish();
    const touch = new Writer().message(field, pan).finish();
    const event = new Writer().message(1, touch).finish();
    return new Writer().message(MSG.pointer_device_event, event).finish();
}
/**
 * A pinch, as a trackpad produces.
 *
 * The change since the last one, times a thousand, as the field is defined; a
 * zero ends the gesture.
 */
export function encodeTouchScale(deltaScale) {
    const scale = Math.round(deltaScale * 1000);
    const update = new Writer().int32Present(1, scale).finish();
    const touch = new Writer().message(1, update).finish();
    const event = new Writer().message(1, touch).finish();
    return new Writer().message(MSG.pointer_device_event, event).finish();
}
/**
 * Says which Windows session to work in.
 *
 * A machine with more than one - a terminal server, or a desktop someone is on
 * over RDP - stops after login and starts nothing until it is told. Choosing a
 * session other than the one it is currently in makes it move there, which
 * drops this connection; reconnecting lands in the chosen session.
 */
export function encodeSelectedSid(sid) {
    return miscUint(35, sid);
}
export function encodeToggleVirtualDisplay(display, on) {
    return misc(32, new Writer().int32(1, display).bool(2, on).finish());
}
/**
 * What the machine allows, as it changes during a session.
 *
 * The person at the machine can revoke any of these while connected, and the
 * machine announces it. Ignoring the announcement leaves a console still
 * offering things that will now be silently dropped.
 */
export const Permission = {
    Keyboard: 0, Clipboard: 2, Audio: 3, File: 4,
    Restart: 5, Recording: 6, BlockInput: 7, PrivacyMode: 8,
};
export const PERMISSION_NAMES = {
    0: "keyboard", 2: "clipboard", 3: "audio", 4: "file",
    5: "restart", 6: "recording", 7: "blockInput", 8: "privacyMode",
};
export function parsePermissionInfo(raw) {
    let permission = -1;
    let enabled = false;
    eachField(raw, (field, _wire, r) => {
        if (field === 1) {
            permission = Number(r.varint());
            return true;
        }
        if (field === 2) {
            enabled = r.varint() !== 0n;
            return true;
        }
        return;
    });
    return { permission, enabled };
}
export const PrivacyModeState = {
    Unknown: 0, OnByOther: 2, NotSupported: 3, OnSucceeded: 4,
    OnFailedDenied: 5, OnFailedPlugin: 6, OnFailed: 7,
    OffSucceeded: 8, OffByPeer: 9, OffFailed: 10, OffUnknown: 11,
};
export const BlockInputState = {
    Unknown: 0, OnSucceeded: 2, OnFailed: 3, OffSucceeded: 4, OffFailed: 5,
};
export function parseBackNotification(raw) {
    const out = { details: "", implKey: "" };
    eachField(raw, (field, _wire, r) => {
        switch (field) {
            case 1:
                out.privacyModeState = Number(r.varint());
                return true;
            case 2:
                out.blockInputState = Number(r.varint());
                return true;
            case 3:
                out.details = r.string();
                return true;
            case 4:
                out.implKey = r.string();
                return true;
            default: return;
        }
    });
    return out;
}
export function encodeAudioFrame(data) {
    const inner = new Writer().bytes(1, data).finish();
    return new Writer().message(MSG.audio_frame, inner).finish();
}
/**
 * Chooses which displays the peer captures.
 *
 * `set` replaces the whole selection, which is how an all-screens view is
 * requested: name every display at once and frames arrive for each, tagged
 * with their display index.
 */
export function encodeCaptureDisplays(opts) {
    const w = new Writer();
    if (opts.add?.length)
        w.packedVarints(1, opts.add);
    if (opts.sub?.length)
        w.packedVarints(2, opts.sub);
    if (opts.set?.length)
        w.packedVarints(3, opts.set);
    return misc(30, w.finish());
}
/**
 * Types text at the peer, for fields that ignore a clipboard paste.
 *
 * `seq` carries a literal string the peer types, which keeps the remote
 * keyboard layout out of it entirely - no scancodes, no dead keys, no
 * mismatch between a French keyboard here and a US one there.
 *
 * Runs of ordinary characters go in one event; Enter and Tab cannot, because
 * `seq` types a character and these have to be the keys themselves.
 */
export function encodeTextAsKeystrokes(text) {
    const out = [];
    let run = "";
    const flush = () => {
        if (!run)
            return;
        out.push(encodeKeyEvent({ down: true, press: true, seq: run }));
        run = "";
    };
    for (const ch of text) {
        if (ch === "\r")
            continue; // a CRLF is one Return, not two
        if (ch === "\n") {
            flush();
            out.push(encodeKeyEvent({ down: true, press: true, controlKey: ControlKey.Return }));
            continue;
        }
        if (ch === "\t") {
            flush();
            out.push(encodeKeyEvent({ down: true, press: true, controlKey: ControlKey.Tab }));
            continue;
        }
        run += ch;
    }
    flush();
    return out;
}
/** Terminal --------------------------------------------------------------- */
function terminalAction(field, inner) {
    const ta = new Writer().message(field, inner).finish();
    return new Writer().message(MSG.terminal_action, ta).finish();
}
export function encodeTerminalOpen(terminalId, rows, cols) {
    return terminalAction(1, new Writer().int32(1, terminalId).uint32(2, rows).uint32(3, cols).finish());
}
/** Keystrokes typed into the terminal. */
export function encodeTerminalData(terminalId, data) {
    return terminalAction(2, new Writer().int32(1, terminalId).bytes(2, data).bool(3, false).finish());
}
export function encodeTerminalResize(terminalId, rows, cols) {
    return terminalAction(3, new Writer().int32(1, terminalId).uint32(2, rows).uint32(3, cols).finish());
}
export function encodeTerminalClose(terminalId) {
    return terminalAction(4, new Writer().int32(1, terminalId).finish());
}
/** File transfer --------------------------------------------------------- */
function fileAction(field, inner) {
    const fa = new Writer().message(field, inner).finish();
    return new Writer().message(MSG.file_action, fa).finish();
}
export function encodeReadDir(path, includeHidden = false) {
    return fileAction(1, new Writer().string(1, path).bool(2, includeHidden).finish());
}
/** Asks the peer to send us a file (download). */
export function encodeFileSendRequest(id, path, fileNum = 0, includeHidden = false) {
    return fileAction(2, new Writer().int32(1, id).string(2, path).bool(3, includeHidden).int32(4, fileNum).finish());
}
function encodeFileEntry(e) {
    return new Writer()
        .int32(1, e.entryType)
        .string(2, e.name)
        .int64(4, e.size)
        .int64(5, e.modifiedTime)
        .finish();
}
/** Tells the peer we are about to upload files into `path`. */
export function encodeFileReceiveRequest(id, path, files, fileNum = 0, totalSize = 0) {
    const w = new Writer().int32(1, id).string(2, path);
    for (const f of files)
        w.message(3, encodeFileEntry(f));
    w.int32(4, fileNum).int64(5, totalSize);
    return fileAction(3, w.finish());
}
export function encodeFileDirCreate(id, path) {
    return fileAction(4, new Writer().int32(1, id).string(2, path).finish());
}
export function encodeFileRemoveDir(id, path, recursive = true) {
    return fileAction(5, new Writer().int32(1, id).string(2, path).bool(3, recursive).finish());
}
export function encodeFileRemoveFile(id, path, fileNum = 0) {
    return fileAction(6, new Writer().int32(1, id).string(2, path).sint32(3, fileNum).finish());
}
export function encodeFileRename(id, path, newName) {
    return fileAction(10, new Writer().int32(1, id).string(2, path).string(3, newName).finish());
}
export function encodeFileCancel(id) {
    return fileAction(8, new Writer().int32(1, id).finish());
}
/** Confirms (or skips) a file the peer offered, resuming at `offsetBlk`. */
export function encodeFileSendConfirm(id, fileNum, opts = {}) {
    const w = new Writer().int32(1, id).sint32(2, fileNum);
    if (opts.skip)
        w.bool(3, true);
    else
        w.uint32(4, opts.offsetBlk ?? 0);
    return fileAction(9, w.finish());
}
/** A chunk of an upload. */
export function encodeFileBlock(id, fileNum, data, blkId, compressed = false) {
    const inner = new Writer()
        .int32(1, id)
        .sint32(2, fileNum)
        .bytes(3, data)
        .bool(4, compressed)
        .uint32(5, blkId)
        .finish();
    const fr = new Writer().message(2, inner).finish();
    return new Writer().message(MSG.file_response, fr).finish();
}
/** Signals the end of an uploaded file. */
export function encodeFileDone(id, fileNum) {
    const inner = new Writer().int32(1, id).sint32(2, fileNum).finish();
    const fr = new Writer().message(4, inner).finish();
    return new Writer().message(MSG.file_response, fr).finish();
}
/** An empty Message, used as a keep-alive / handshake nudge. */
export function encodeEmpty() {
    return new Uint8Array();
}
/**
 * Reads the machine's capabilities out of PeerInfo.
 *
 * `platform_additions` is free-form JSON the machine fills in, so everything
 * here is optional and a machine that says nothing simply offers nothing.
 */
export function peerCapabilities(info) {
    let extra = {};
    try {
        const parsed = JSON.parse(info.platformAdditions || "{}");
        if (parsed && typeof parsed === "object")
            extra = parsed;
    }
    catch {
        // A machine that sends something unreadable just gets the defaults.
    }
    // Listed as [key, translation-tag] pairs.
    const raw = Array.isArray(extra.supported_privacy_mode_impl)
        ? extra.supported_privacy_mode_impl
        : [];
    const privacyModeImpls = raw
        .filter((p) => Array.isArray(p) && typeof p[0] === "string")
        .map(([key, tag]) => ({ key, label: privacyImplLabel(key, tag) }));
    return {
        privacyModeImpls,
        // A machine may name no implementation and still do it, in which case an
        // empty key lets it choose - so the feature is not hidden on that basis.
        canBlankScreen: info.features.privacyMode || privacyModeImpls.length > 0,
        virtualDisplay: typeof extra.idd_impl === "string" ? extra.idd_impl : null,
        viewCamera: extra.support_view_camera === true,
    };
}
/**
 * Plain-English names for the privacy-mode implementations RustDesk ships.
 *
 * All of them blank the machine's own screen; they differ in how. The first
 * two cover the real screen with a black window that is excluded from capture,
 * so you keep seeing the desktop while the person there sees black. The third
 * moves the desktop to a virtual display, leaving the real one showing
 * nothing - the only one that works when the black window can be dismissed.
 */
function privacyImplLabel(key, tag) {
    switch (key) {
        case "privacy_mode_impl_exclude_from_capture":
            return "Blank the screen (cover it)";
        case "privacy_mode_impl_mag":
            return "Blank the screen (cover it, older Windows)";
        case "privacy_mode_impl_virtual_display":
            return "Blank the screen (move the desktop to a virtual display)";
        default:
            return tag || key;
    }
}
/**
 * Decodes a `Message`. Only the first recognised union member is returned;
 * later unknown fields (which newer peers append) are ignored rather than
 * overwriting the result.
 */
export function decodeMessage(buf) {
    let out = { kind: "unknown", field: 0 };
    eachField(buf, (field, type, r) => {
        if (type !== 2 /* WireType.Bytes */ && field !== MSG.cursor_id) {
            if (out.kind === "unknown")
                out = { kind: "unknown", field };
            return;
        }
        switch (field) {
            case MSG.signed_id:
                out = { kind: "signed_id", id: parseSignedId(r.bytes()) };
                return true;
            case MSG.hash:
                out = parseHash(r.bytes());
                return true;
            case MSG.login_response:
                out = parseLoginResponse(r.bytes());
                return true;
            case MSG.peer_info:
                out = { kind: "peer_info", peerInfo: parsePeerInfo(r.bytes()) };
                return true;
            case MSG.video_frame:
                out = parseVideoFrame(r.bytes());
                return true;
            case MSG.cursor_data:
                out = parseCursorData(r.bytes());
                return true;
            case MSG.cursor_position:
                out = parseCursorPosition(r.bytes());
                return true;
            case MSG.cursor_id:
                out = { kind: "cursor_id", id: r.varint() };
                return true;
            case MSG.clipboard:
                out = parseClipboard(r.bytes());
                return true;
            case MSG.multi_clipboards:
                out = parseMultiClipboards(r.bytes());
                return true;
            case MSG.switch_sides_request:
                r.bytes();
                out = { kind: "switch_sides_request" };
                return true;
            case MSG.test_delay:
                out = parseTestDelay(r.bytes());
                return true;
            case MSG.misc:
                out = parseMisc(r.bytes());
                return true;
            case MSG.message_box:
                out = parseMessageBox(r.bytes());
                return true;
            case MSG.audio_frame:
                out = parseAudioFrame(r.bytes());
                return true;
            case MSG.file_response:
                out = parseFileResponse(r.bytes());
                return true;
            case MSG.terminal_response:
                out = parseTerminalResponse(r.bytes());
                return true;
            case MSG.screenshot_response: {
                let sid = "", message = "";
                let data = new Uint8Array();
                eachField(r.bytes(), (ff, _tt, rr) => {
                    if (ff === 1) {
                        sid = rr.string();
                        return true;
                    }
                    if (ff === 2) {
                        message = rr.string();
                        return true;
                    }
                    if (ff === 3) {
                        data = rr.bytes().slice();
                        return true;
                    }
                    return;
                });
                out = { kind: "screenshot", sid, message, data };
                return true;
            }
            case MSG.voice_call_response: {
                let accepted = false;
                let reqTimestamp = 0n, ackTimestamp = 0n;
                eachField(r.bytes(), (ff, _tt, rr) => {
                    if (ff === 1) {
                        accepted = rr.bool();
                        return true;
                    }
                    if (ff === 2) {
                        reqTimestamp = rr.int64();
                        return true;
                    }
                    if (ff === 3) {
                        ackTimestamp = rr.int64();
                        return true;
                    }
                    return;
                });
                out = { kind: "voice_call_response", accepted, reqTimestamp, ackTimestamp };
                return true;
            }
            default:
                if (out.kind === "unknown")
                    out = { kind: "unknown", field };
                return;
        }
    });
    return out;
}
function parseSignedId(buf) {
    let id = new Uint8Array();
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            id = r.bytes().slice();
            return true;
        }
        return;
    });
    return id;
}
function parseHash(buf) {
    let salt = "", challenge = "";
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            salt = r.string();
            return true;
        }
        if (f === 2) {
            challenge = r.string();
            return true;
        }
        return;
    });
    return { kind: "hash", salt, challenge };
}
function parseDisplayInfo(buf) {
    const d = {
        x: 0, y: 0, width: 0, height: 0, name: "", online: false,
        cursorEmbedded: false, scale: 1,
    };
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                d.x = r.sint32();
                return true;
            case 2:
                d.y = r.sint32();
                return true;
            case 3:
                d.width = r.int32();
                return true;
            case 4:
                d.height = r.int32();
                return true;
            case 5:
                d.name = r.string();
                return true;
            case 6:
                d.online = r.bool();
                return true;
            case 7:
                d.cursorEmbedded = r.bool();
                return true;
            case 8:
                d.originalResolution = parseResolution(r.bytes());
                return true;
            default: return;
        }
    });
    return d;
}
function parsePeerInfo(buf) {
    const p = {
        username: "", hostname: "", platform: "", displays: [], currentDisplay: 0,
        version: "", sasEnabled: false, platformAdditions: "",
        features: { privacyMode: false, terminal: false },
        resolutions: [],
        windowsSessions: { sessions: [], currentSid: 0 },
        encoding: { h264: false, h265: false, vp8: false, av1: false },
    };
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                p.username = r.string();
                return true;
            case 2:
                p.hostname = r.string();
                return true;
            case 3:
                p.platform = r.string();
                return true;
            case 4:
                p.displays.push(parseDisplayInfo(r.bytes()));
                return true;
            case 5:
                p.currentDisplay = r.int32();
                return true;
            case 6:
                p.sasEnabled = r.bool();
                return true;
            case 7:
                p.version = r.string();
                return true;
            case 10: {
                const enc = r.bytes();
                eachField(enc, (ef, _et, er) => {
                    switch (ef) {
                        case 1:
                            p.encoding.h264 = er.bool();
                            return true;
                        case 2:
                            p.encoding.h265 = er.bool();
                            return true;
                        case 3:
                            p.encoding.vp8 = er.bool();
                            return true;
                        case 4:
                            p.encoding.av1 = er.bool();
                            return true;
                        default: return;
                    }
                });
                return true;
            }
            case 9:
                eachField(r.bytes(), (ff, _ft, fr) => {
                    if (ff === 1) {
                        p.features.privacyMode = fr.varint() !== 0n;
                        return true;
                    }
                    if (ff === 2) {
                        p.features.terminal = fr.varint() !== 0n;
                        return true;
                    }
                    return;
                });
                return true;
            case 11:
                eachField(r.bytes(), (rf, _rt, rr) => {
                    if (rf === 1) {
                        p.resolutions.push(parseResolution(rr.bytes()));
                        return true;
                    }
                    return;
                });
                return true;
            case 12:
                p.platformAdditions = r.string();
                return true;
            case 13:
                eachField(r.bytes(), (wf, _wt, wr) => {
                    if (wf === 1) {
                        const entry = { sid: 0, name: "" };
                        eachField(wr.bytes(), (sf, _st, sr) => {
                            if (sf === 1) {
                                entry.sid = sr.uint32();
                                return true;
                            }
                            if (sf === 2) {
                                entry.name = sr.string();
                                return true;
                            }
                            return;
                        });
                        p.windowsSessions.sessions.push(entry);
                        return true;
                    }
                    if (wf === 2) {
                        p.windowsSessions.currentSid = wr.uint32();
                        return true;
                    }
                    return;
                });
                return true;
            default: return;
        }
    });
    return p;
}
function parseLoginResponse(buf) {
    let error;
    let peerInfo;
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            error = r.string();
            return true;
        }
        if (f === 2) {
            peerInfo = parsePeerInfo(r.bytes());
            return true;
        }
        return;
    });
    return { kind: "login_response", error, peerInfo };
}
function parseEncodedFrames(buf) {
    const frames = [];
    eachField(buf, (f, _t, r) => {
        if (f !== 1)
            return;
        const one = r.bytes();
        const frame = { data: new Uint8Array(), key: false, pts: 0n };
        eachField(one, (ff, _ft, fr) => {
            switch (ff) {
                case 1:
                    frame.data = fr.bytes().slice();
                    return true;
                case 2:
                    frame.key = fr.bool();
                    return true;
                case 3:
                    frame.pts = fr.int64();
                    return true;
                default: return;
            }
        });
        frames.push(frame);
        return true;
    });
    return frames;
}
function parseVideoFrame(buf) {
    let codec = "";
    let frames = [];
    let display = 0;
    eachField(buf, (f, _t, r) => {
        const name = VIDEO_CODEC[f];
        if (name) {
            codec = name;
            frames = parseEncodedFrames(r.bytes());
            return true;
        }
        if (f === 14) {
            display = r.int32();
            return true;
        }
        return;
    });
    return { kind: "video_frame", codec, display, frames };
}
function parseCursorData(buf) {
    let id = 0n, hotx = 0, hoty = 0, width = 0, height = 0;
    let colors = new Uint8Array();
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                id = r.varint();
                return true;
            case 2:
                hotx = r.sint32();
                return true;
            case 3:
                hoty = r.sint32();
                return true;
            case 4:
                width = r.int32();
                return true;
            case 5:
                height = r.int32();
                return true;
            case 6:
                colors = r.bytes().slice();
                return true;
            default: return;
        }
    });
    return { kind: "cursor_data", id, hotx, hoty, width, height, colors };
}
function parseCursorPosition(buf) {
    let x = 0, y = 0;
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            x = r.sint32();
            return true;
        }
        if (f === 2) {
            y = r.sint32();
            return true;
        }
        return;
    });
    return { kind: "cursor_position", x, y };
}
/** ClipboardFormat in message.proto. */
export const ClipboardFormat = {
    Text: 0, Rtf: 1, Html: 2, ImageRgba: 21, ImagePng: 22, ImageSvg: 23, Special: 31,
};
function parseOneClipboard(buf) {
    let compress = false;
    let content = new Uint8Array();
    let format = ClipboardFormat.Text;
    let width = 0;
    let height = 0;
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            compress = r.bool();
            return true;
        }
        if (f === 2) {
            content = r.bytes().slice();
            return true;
        }
        if (f === 3) {
            width = r.int32();
            return true;
        }
        if (f === 4) {
            height = r.int32();
            return true;
        }
        if (f === 5) {
            format = Number(r.varint());
            return true;
        }
        return;
    });
    return { compress, content, format, width, height };
}
function parseClipboard(buf) {
    const one = parseOneClipboard(buf);
    return { kind: "clipboard", clipboards: [one], ...one };
}
/**
 * Several formats of the same copy, in one message.
 *
 * A machine sends text, HTML and an image together so the receiver can take
 * whichever it can use. Reading only the first - or only the plain-text
 * message - loses everything but the text.
 */
function parseMultiClipboards(buf) {
    const clipboards = [];
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            clipboards.push(parseOneClipboard(r.bytes()));
            return true;
        }
        return;
    });
    const first = clipboards.find((c) => c.format === ClipboardFormat.Text) ?? clipboards[0];
    return {
        kind: "clipboard",
        clipboards,
        compress: first?.compress ?? false,
        content: first?.content ?? new Uint8Array(),
        format: first?.format ?? ClipboardFormat.Text,
        width: first?.width ?? 0,
        height: first?.height ?? 0,
    };
}
function parseTestDelay(buf) {
    let time = 0n, fromClient = false, lastDelay = 0, targetBitrate = 0;
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            time = r.int64();
            return true;
        }
        if (f === 2) {
            fromClient = r.bool();
            return true;
        }
        if (f === 3) {
            lastDelay = r.uint32();
            return true;
        }
        if (f === 4) {
            targetBitrate = r.uint32();
            return true;
        }
        return;
    });
    return { kind: "test_delay", time, fromClient, lastDelay, targetBitrate };
}
function parseMisc(buf) {
    let field = 0;
    let raw = new Uint8Array();
    eachField(buf, (f, t, r) => {
        field = f;
        if (t === 2 /* WireType.Bytes */)
            raw = r.bytes().slice();
        else if (t === 0 /* WireType.Varint */) {
            const v = r.varint();
            raw = new Uint8Array([Number(v & 0xffn)]);
        }
        else
            return;
        return true;
    });
    return { kind: "misc", field, raw };
}
function parseMessageBox(buf) {
    let msgtype = "", title = "", text = "", link = "";
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                msgtype = r.string();
                return true;
            case 2:
                title = r.string();
                return true;
            case 3:
                text = r.string();
                return true;
            case 4:
                link = r.string();
                return true;
            default: return;
        }
    });
    return { kind: "message_box", msgtype, title, text, link };
}
function parseAudioFrame(buf) {
    let data = new Uint8Array();
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            data = r.bytes().slice();
            return true;
        }
        return;
    });
    return { kind: "audio_frame", data };
}
function parseFileEntry(buf) {
    const e = {
        entryType: 0, name: "", isHidden: false, size: 0n, modifiedTime: 0n,
    };
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                e.entryType = r.int32();
                return true;
            case 2:
                e.name = r.string();
                return true;
            case 3:
                e.isHidden = r.bool();
                return true;
            case 4:
                e.size = r.int64();
                return true;
            case 5:
                e.modifiedTime = r.int64();
                return true;
            default: return;
        }
    });
    return e;
}
function parseFileResponse(buf) {
    let out = { kind: "unknown", field: MSG.file_response };
    eachField(buf, (f, t, r) => {
        if (t !== 2 /* WireType.Bytes */)
            return;
        const inner = r.bytes();
        switch (f) {
            case 1: {
                let id = 0, path = "";
                const entries = [];
                eachField(inner, (df, _dt, dr) => {
                    if (df === 1) {
                        id = dr.int32();
                        return true;
                    }
                    if (df === 2) {
                        path = dr.string();
                        return true;
                    }
                    if (df === 3) {
                        entries.push(parseFileEntry(dr.bytes()));
                        return true;
                    }
                    return;
                });
                out = { kind: "file_dir", id, path, entries };
                return true;
            }
            case 2: {
                let id = 0, fileNum = 0, blkId = 0, compressed = false;
                let data = new Uint8Array();
                eachField(inner, (bf, _bt, br) => {
                    switch (bf) {
                        case 1:
                            id = br.int32();
                            return true;
                        case 2:
                            fileNum = br.sint32();
                            return true;
                        case 3:
                            data = br.bytes().slice();
                            return true;
                        case 4:
                            compressed = br.bool();
                            return true;
                        case 5:
                            blkId = br.uint32();
                            return true;
                        default: return;
                    }
                });
                out = { kind: "file_block", id, fileNum, data, compressed, blkId };
                return true;
            }
            case 3: {
                let id = 0, error = "", fileNum = 0;
                eachField(inner, (ef, _et, er) => {
                    if (ef === 1) {
                        id = er.int32();
                        return true;
                    }
                    if (ef === 2) {
                        error = er.string();
                        return true;
                    }
                    if (ef === 3) {
                        fileNum = er.sint32();
                        return true;
                    }
                    return;
                });
                out = { kind: "file_error", id, error, fileNum };
                return true;
            }
            case 4: {
                let id = 0, fileNum = 0;
                eachField(inner, (df, _dt, dr) => {
                    if (df === 1) {
                        id = dr.int32();
                        return true;
                    }
                    if (df === 2) {
                        fileNum = dr.sint32();
                        return true;
                    }
                    return;
                });
                out = { kind: "file_done", id, fileNum };
                return true;
            }
            case 5: {
                let id = 0, fileNum = 0, isUpload = false, isIdentical = false;
                let fileSize = 0n, transferredSize = 0n;
                eachField(inner, (df, _dt, dr) => {
                    switch (df) {
                        case 1:
                            id = dr.int32();
                            return true;
                        case 2:
                            fileNum = dr.sint32();
                            return true;
                        case 4:
                            fileSize = dr.int64();
                            return true;
                        case 5:
                            isUpload = dr.bool();
                            return true;
                        case 6:
                            isIdentical = dr.bool();
                            return true;
                        case 7:
                            transferredSize = dr.int64();
                            return true;
                        default: return;
                    }
                });
                out = { kind: "file_digest", id, fileNum, fileSize, isUpload, isIdentical, transferredSize };
                return true;
            }
            default:
                return;
        }
    });
    return out;
}
function parseTerminalResponse(buf) {
    let out = { kind: "unknown", field: MSG.terminal_response };
    eachField(buf, (f, t, r) => {
        if (t !== 2 /* WireType.Bytes */)
            return;
        const inner = r.bytes();
        switch (f) {
            case 1: {
                const v = {
                    kind: "terminal_opened",
                    terminalId: 0, success: false, message: "", pid: 0,
                    serviceId: "", persistentSessions: [], replayOutput: false,
                };
                eachField(inner, (ff, tt, rr) => {
                    switch (ff) {
                        case 1:
                            v.terminalId = rr.int32();
                            return true;
                        case 2:
                            v.success = rr.bool();
                            return true;
                        case 3:
                            v.message = rr.string();
                            return true;
                        case 4:
                            v.pid = rr.uint32();
                            return true;
                        case 5:
                            v.serviceId = rr.string();
                            return true;
                        case 6: {
                            // A repeated int32 arrives packed.
                            if (tt === 2 /* WireType.Bytes */) {
                                const packed = rr.bytes();
                                eachField(packed, () => undefined);
                                const pr = new Reader(packed);
                                while (!pr.eof)
                                    v.persistentSessions.push(Number(pr.varint()));
                            }
                            else {
                                v.persistentSessions.push(rr.int32());
                            }
                            return true;
                        }
                        case 7:
                            v.replayOutput = rr.bool();
                            return true;
                        default: return;
                    }
                });
                out = v;
                return true;
            }
            case 2: {
                let terminalId = 0, compressed = false;
                let data = new Uint8Array();
                eachField(inner, (ff, _tt, rr) => {
                    if (ff === 1) {
                        terminalId = rr.int32();
                        return true;
                    }
                    if (ff === 2) {
                        data = rr.bytes().slice();
                        return true;
                    }
                    if (ff === 3) {
                        compressed = rr.bool();
                        return true;
                    }
                    return;
                });
                out = { kind: "terminal_data", terminalId, data, compressed };
                return true;
            }
            case 3: {
                let terminalId = 0, exitCode = 0;
                eachField(inner, (ff, _tt, rr) => {
                    if (ff === 1) {
                        terminalId = rr.int32();
                        return true;
                    }
                    if (ff === 2) {
                        exitCode = rr.int32();
                        return true;
                    }
                    return;
                });
                out = { kind: "terminal_closed", terminalId, exitCode };
                return true;
            }
            case 4: {
                let terminalId = 0, message = "";
                eachField(inner, (ff, _tt, rr) => {
                    if (ff === 1) {
                        terminalId = rr.int32();
                        return true;
                    }
                    if (ff === 2) {
                        message = rr.string();
                        return true;
                    }
                    return;
                });
                out = { kind: "terminal_error", terminalId, message };
                return true;
            }
            default:
                return;
        }
    });
    return out;
}
/** Misc field numbers we react to on the incoming side. */
export const MISC = {
    chat_message: 4,
    switch_display: 5,
    permission_info: 6,
    option: 7,
    audio_format: 8,
    close_reason: 9,
    back_notification: 13,
    uac: 15,
    foreground_window_elevated: 16,
    elevation_response: 19,
    portable_service_running: 20,
    supported_encoding: 34,
};
export function parseCloseReason(raw) {
    return new TextDecoder().decode(raw);
}
/** ChatMessage { string text = 1 } */
export function parseChatMessage(raw) {
    let text = "";
    eachField(raw, (f, _t, r) => {
        if (f === 1) {
            text = r.string();
            return true;
        }
        return;
    });
    return text;
}
/** AudioFormat { sample_rate = 1; channels = 2 } */
export function parseAudioFormat(raw) {
    let sampleRate = 48000;
    let channels = 2;
    eachField(raw, (f, _t, r) => {
        if (f === 1) {
            sampleRate = r.uint32();
            return true;
        }
        if (f === 2) {
            channels = r.uint32();
            return true;
        }
        return;
    });
    return { sampleRate, channels };
}
export function parseSwitchDisplay(raw) {
    const s = {
        display: 0, x: 0, y: 0, width: 0, height: 0, cursorEmbedded: false,
    };
    eachField(raw, (f, _t, r) => {
        switch (f) {
            case 1:
                s.display = r.int32();
                return true;
            case 2:
                s.x = r.sint32();
                return true;
            case 3:
                s.y = r.sint32();
                return true;
            case 4:
                s.width = r.int32();
                return true;
            case 5:
                s.height = r.int32();
                return true;
            case 6:
                s.cursorEmbedded = r.bool();
                return true;
            default: return;
        }
    });
    return s;
}
